import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useEffect, useState } from 'react'
import { Dialogs, useDialogs } from '../Contexts/DialogsContext'
import { useGlobalContext } from '../Contexts/GlobalContext'
import AnimatedDialog from '../Dialogs/Common/AnimatedDialog'
import SignUp from '../Dialogs/Forms/SignUp'
import { GroupNames } from '../globalHeader/GlobalHeader'
import { commonNavigate } from '../helpers/commonNavigate'
import { formatTimeDifference } from '../helpers/fortmatTimeDifference'
import { useScreenSize } from '../hooks/useScreenSize'
import { getAssetUrl } from '../utils/assetUrl'
import { HintBonusIcon } from './assets/HintBonusIcon'
import { BonusAmount, BonusData, BonusPropositions, BonusType, DailyBonus } from './bonusTypes'
import HintWithText from './Common/HintWithText'
import { getBonusAdditionalInfo } from './walletAPI'

interface IProps {
    bonus: DailyBonus
    claimBonus: (bonusType: BonusType, amount: number) => void
    additionalData: Array<BonusAmount>
}

const DailyBonusDialog: React.FC<IProps> = (props) => {
    const { bonus, claimBonus, additionalData } = props
    const { userType, navigate, websiteURL } = useGlobalContext()
    const { registerDialog, unregisterDialog } = useDialogs()
    const { isMobile } = useScreenSize()
    const [data, setData] = useState<BonusData>()
    const [loadFailed, setLoadFailed] = useState(false)
    const [timerText, setTimerText] = useState('')
    const headerAnimationUrl = getAssetUrl('/animations/daily-bonus/logo-daily-bonus.lottie')
    const dailyBonusBgUrl = getAssetUrl('/images/daily-bonus-bg.webp')

    // Preload background image
    useEffect(() => {
        if (dailyBonusBgUrl) {
            const img = new window.Image()
            img.src = dailyBonusBgUrl
        }
    }, [dailyBonusBgUrl])

    useEffect(() => {
        if (data) {
            prepareDialogData()
        }
    }, [bonus])

    const prepareDialogData = async () => {
        try {
            const result = await getBonusAdditionalInfo(bonus.namespace)
            const propositions: Array<BonusPropositions> = []
            const initialAmountKey = additionalData.findIndex((item) => item.userType === userType)
            const nextInitialAmount = additionalData[initialAmountKey + 1]

            if (bonus.ifEligible) {
                const propositionKey = result.findIndex((item) => item.userType === userType)
                const currentProposition = result[propositionKey]
                const nextProposition = result[propositionKey + 1]
                const amount = currentProposition?.amount || 0

                propositions.push({
                    title: 'You Earned:',
                    dailyAmount: amount,
                    icon: <HintBonusIcon />,
                    button: {
                        title: 'Collect',
                        color: 'primary',
                        onClick: () => {
                            claimBonus(bonus.namespace, amount)
                            unregisterDialog(Dialogs.DAILY_BONUS)
                        },
                    },
                })

                if (nextProposition) {
                    propositions.push({
                        title: 'Special Offer:',
                        dailyAmount: nextProposition?.amount || 0,
                        initialAmount: nextInitialAmount?.amount || 0,
                        icon: <HintBonusIcon />,
                        button: {
                            title: userType === GroupNames.GUEST ? 'Join for free' : 'Go Premium',
                            color: 'secondary',
                            onClick: () => {
                                if (userType === GroupNames.GUEST) {
                                    unregisterDialog(Dialogs.DAILY_BONUS)
                                    registerDialog(Dialogs.SIGNUP, <SignUp />)
                                } else {
                                    unregisterDialog(Dialogs.DAILY_BONUS)
                                    commonNavigate(`${websiteURL}/memberships`, navigate)
                                }
                            },
                        },
                    })
                }
            } else {
                propositions.push({
                    title: userType !== GroupNames.SUBSCRIBED ? 'Want more now?' : undefined,
                    dailyAmount: 0,
                    initialAmount: nextInitialAmount?.amount || 0,
                    icon: undefined,
                    button: {
                        title:
                            userType === GroupNames.GUEST
                                ? 'Join free now'
                                : userType === GroupNames.MEMBER
                                ? 'Go Premium'
                                : 'Close',
                        color: 'primary',
                        onClick: () => {
                            if (userType === GroupNames.GUEST) {
                                unregisterDialog(Dialogs.DAILY_BONUS)
                                registerDialog(Dialogs.SIGNUP, <SignUp />)
                            } else if (userType === GroupNames.MEMBER) {
                                unregisterDialog(Dialogs.DAILY_BONUS)
                                commonNavigate(`${websiteURL}/memberships`, navigate)
                            } else {
                                unregisterDialog(Dialogs.DAILY_BONUS)
                            }
                        },
                    },
                })
            }
            const newData: BonusData = {
                isReady: bonus.ifEligible,
                content: {
                    showLogin: userType === GroupNames.GUEST,
                    nextBonusTime: bonus.availableIn,
                    timerTitle: 'Next Bonus Ready In:',
                    propositions: propositions,
                },
            }
            setData(newData)
        } catch (error) {
            console.error('Error fetching bonus additional info:', error)
            // stay open in a retry state: the header chest gives no other feedback when this fails
            setData(undefined)
            setLoadFailed(true)
        }
    }

    useEffect(() => {
        if (!data) {
            prepareDialogData()
        }
    }, [])

    useEffect(() => {
        if (data && !data.isReady && data.content.nextBonusTime) {
            setTimerText(formatTimeDifference(new Date(data.content.nextBonusTime)))
            const interval = setInterval(() => {
                setTimerText(formatTimeDifference(new Date(data.content.nextBonusTime)))
            }, 1000)
            return () => clearInterval(interval)
        }
    }, [data])

    if (!data && !loadFailed) {
        return null
    }

    return (
        <AnimatedDialog
            name={Dialogs.DAILY_BONUS}
            borderColor="secondary.main"
            paddingLess
            triggerImmediately
            paperProps={{
                animationOrImage: {
                    src: headerAnimationUrl,
                    verticalPosition: {
                        xs: '-55px',
                        md: '-110px',
                    },
                    horizontalPosition: {
                        xs: '27%',
                        md: '27%',
                    },
                },
            }}
        >
            <Stack
                sx={{
                    backgroundImage: `url(${dailyBonusBgUrl})`,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'top',
                    width: '100%',
                    height: { xs: '160px', md: '253px' },
                    borderRadius: '0 !important',
                    borderBottom: '1px solid',
                    borderColor: 'secondary.main',
                }}
            />
            <Stack
                sx={{
                    px: { xs: '0px', md: '20px' },
                    pt: { xs: '0px', md: '20px' },
                    pb: { xs: '20px', md: '40px' },
                    alignItems: 'center',
                    gap: isMobile ? 1 : 2,
                }}
            >
                {!data && (
                    <>
                        <Typography variant={isMobile ? 'body2' : 'body1'}>
                            Couldn't load your daily bonus. Please try again.
                        </Typography>
                        <Button
                            variant="contained"
                            size={isMobile ? 'medium' : 'large'}
                            sx={{ minWidth: { xs: '120px', md: '160px' } }}
                            onClick={() => prepareDialogData()}
                        >
                            Try again
                        </Button>
                    </>
                )}
                {data && !data.isReady && data.content.nextBonusTime && (
                    <Stack gap={1}>
                        <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight={700}>
                            {data.content.timerTitle}
                        </Typography>
                        <Typography variant={isMobile ? 'body2' : 'body1'}>{timerText}</Typography>
                    </Stack>
                )}
                <Stack direction="row" gap={2}>
                    {data?.content.propositions.map((item, index) => (
                        <Stack key={index} gap={1} alignItems="center">
                            <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight={700}>
                                {item.title}
                            </Typography>
                            {item.icon ? (
                                <>
                                    {item.initialAmount ? (
                                        <Stack direction="row" gap={1} alignItems="center">
                                            <Typography
                                                variant={isMobile ? 'body2' : 'body1'}
                                            >{`${item.initialAmount}x`}</Typography>
                                            {item.icon}
                                            <Typography variant={isMobile ? 'body2' : 'body1'}>Hints Now</Typography>
                                        </Stack>
                                    ) : null}
                                    <Stack flexGrow={1} direction="row" gap={1} alignItems="center">
                                        <Typography
                                            variant={isMobile ? 'body2' : 'body1'}
                                        >{`${item.dailyAmount}x`}</Typography>
                                        {item.icon}
                                        <Typography variant={isMobile ? 'body2' : 'body1'}>{`Hints ${
                                            item.initialAmount ? 'Daily' : ''
                                        }`}</Typography>
                                    </Stack>
                                </>
                            ) : userType !== GroupNames.SUBSCRIBED ? (
                                <HintWithText amount={item.initialAmount} />
                            ) : null}
                            <Button
                                variant="contained"
                                size={isMobile ? 'medium' : 'large'}
                                sx={{ minWidth: { xs: '120px', md: '160px' } }}
                                onClick={item.button.onClick}
                                color={item.button.color}
                            >
                                {item.button.title}
                            </Button>
                        </Stack>
                    ))}
                </Stack>
            </Stack>
        </AnimatedDialog>
    )
}

export default DailyBonusDialog
